import { useMemo } from 'react'
import { generatePath } from 'react-router-dom'

import { useGetTeamSubscription } from '@queries/Billing'

import routes from '@src/Routes/routes'
import { FeaturesPaywall, FeaturesToPaywall, UNGATED_FEATURES, getPaywallPath } from '@helpers/paywall'
import { Feature } from '@type/features'
import { Integration } from '@type/integration'

// The pure mapping lives in @helpers/paywall; re-exported here so the many
// existing importers keep working.
export { FeaturesPaywall, FeaturesToPaywall, getPaywallPath }

const AccountingIntegrationToPaywall: Partial<Record<Integration, FeaturesPaywall>> = {
  [Integration.xero]: FeaturesPaywall.Xero,
  [Integration.softledger]: FeaturesPaywall.Softledger,
  [Integration.quickbooks]: FeaturesPaywall.Quickbooks,
  [Integration.netsuite]: FeaturesPaywall.Netsuite,
}

type FeatureWithPaywall = { hidden: boolean; paywall?: FeaturesPaywall; paywallPath?: string }

export const useGetHiddenWithPaywallFeatures = () => {
  const { data: subscription } = useGetTeamSubscription()

  return useMemo(() => {
    const defaultValues = Object.values(Feature).reduce<Record<Feature, FeatureWithPaywall>>((acc, feature) => {
      return {
        ...acc,
        [feature]: { hidden: false },
      }
    }, {} as Record<Feature, FeatureWithPaywall>)

    return Object.entries(subscription?.plan_features || {}).reduce<Record<Feature, FeatureWithPaywall>>(
      (acc, [featureName, planAvailable]) => {
        // Object.entries erases the key type; cast once rather than at each use.
        const feature = featureName as Feature
        const available = planAvailable || UNGATED_FEATURES.has(feature)
        const paywall = !available ? FeaturesToPaywall[feature] : undefined
        return {
          ...acc,
          [feature]: {
            hidden: !available,
            paywall: paywall,
            paywallPath: paywall ? getPaywallPath(paywall) : undefined,
          },
        }
      },
      defaultValues,
    )
  }, [subscription?.plan_features])
}

export const useGetIsFeatureAvailable = (feature?: Feature, { integration }: { integration?: Integration } = {}) => {
  const { data: subscription } = useGetTeamSubscription()

  return useMemo(() => {
    // Available on every plan — answer without reading plan_features, which
    // still reports these false. Everything below stays reachable: it is what
    // a feature resolves to again once it is taken back out of the set.
    if (feature && UNGATED_FEATURES.has(feature)) {
      return { available: true, paywall: undefined, paywallPath: undefined }
    }
    if (feature === Feature.accountingIntegrations) {
      const available = subscription?.plan_features?.[feature] ?? true

      const paywall = integration ? AccountingIntegrationToPaywall[integration] : FeaturesPaywall.Integrations
      return {
        available,
        paywall: !available ? paywall : undefined,
        paywallPath: !available && paywall ? getPaywallPath(paywall) : undefined,
      }
    }
    const paywall = feature ? FeaturesToPaywall[feature] : undefined

    const available = feature ? subscription?.plan_features?.[feature] ?? true : true
    return {
      available,
      paywall: !available ? paywall : undefined,
      paywallPath: !available && paywall ? generatePath(routes.private.paywall, { feature: paywall }) : undefined,
    }
  }, [subscription])
}
